import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useUpdateProductPriceListItemMutation, useRefreshProductPriceListMutation } from 'features/products/redux/productApiSlice';
import { ProductPriceListItem } from 'features/products/types/productPriceListTypes';
import { RowSelectionState } from '@tanstack/react-table';


interface UsePriceListEditProps {
  selectedProductRowId: number;
  priceListData: ProductPriceListItem[];
  initialUpdatedTimestamps: { [rowId: number]: string };
  setInitialUpdatedTimestamps: (timestamps: { [rowId: number]: string }) => void;
  getProductPriceListAuditDetails: any;
  refetchPriceList: () => void;
  resetInitialTimestamps: () => void;
}

export const usePriceListEdit = ({
  priceListData,
  selectedProductRowId,
  initialUpdatedTimestamps,
  setInitialUpdatedTimestamps,
  getProductPriceListAuditDetails,
  refetchPriceList,
  resetInitialTimestamps }: UsePriceListEditProps) => {

  // Edit state
  const [updatingItems, setUpdatingItems] = useState<Set<number>>(new Set());
  const [changedCells, setChangedCells] = useState<{ [key: string]: any }>({});
  const [isRefreshingPriceList, setIsRefreshingPriceList] = useState(false);

  // API mutation
  const [updatePriceListItem] = useUpdateProductPriceListItemMutation();
  const [refreshProductPriceListMutation] = useRefreshProductPriceListMutation();

  // Derived state
  const hasUnsavedChanges = Object.keys(changedCells).length > 0;

  // Define which fields should be treated as numbers
  const numericFields = [
    'unitPurchasePrice',
    'unitPurchasePriceInc',
    'unitPrice',
    'priceInc',
    'oldPriceInc',
    'markupPercent',
    'sellRangeMax',
    'sellRangeMin',
    'startingPrice'
  ];

  // Helper function to convert value to appropriate type
  const convertValueToCorrectType = (field: string, value: string) => {
    if (numericFields.includes(field)) {
      const numValue = parseFloat(value);
      return value === '' ? null : isNaN(numValue) ? null : numValue;
    }
    return value;
  };

  // Helper function to get current cell value and modification status
  const getCellInfo = (rowId: number, field: string, originalValue: any) => {
    const cellKey = `${rowId}-${field}`;
    const changedCell = changedCells[cellKey];
    const currentValue = changedCell ? changedCell.newValue : originalValue?.toString() || '';
    const isModified = changedCell !== undefined;
    return { currentValue, isModified };
  };

  // Handle cell changes
  const handleCellChange = useCallback((rowId: number, field: string, newValue: string, originalValue: string) => {
    const cellKey = `${rowId}-${field}`;

    if (newValue === originalValue) {
      // Remove from changes if reverted to original
      setChangedCells(prev => {
        const { [cellKey]: removed, ...rest } = prev;
        return rest;
      });
    } else {
      // Track the change
      setChangedCells(prev => ({
        ...prev,
        [cellKey]: { rowId, field, newValue, originalValue }
      }));
    }
  }, []);

  // Handle save operation
  const handleSave = useCallback(async () => {
    if (!selectedProductRowId || !hasUnsavedChanges) return;

    try {
      // Group changes by row
      const changesByRow: { [rowId: number]: any } = {};
      Object.values(changedCells).forEach((change: any) => {
        if (!changesByRow[change.rowId]) {
          changesByRow[change.rowId] = {};
        }
        changesByRow[change.rowId][change.field] = change.newValue;
      });

      // Check for concurrent modifications before saving
      const concurrentModificationChecks = Object.keys(changesByRow).map(async (rowIdStr) => {
        const rowId = parseInt(rowIdStr);

        try {
          const auditResult = await getProductPriceListAuditDetails({
            productRowId: selectedProductRowId,
            productPriceListRowId: rowId
          }).unwrap();

          const currentUpdatedTime = auditResult.data.updated;
          const initialUpdatedTime = initialUpdatedTimestamps[rowId];

          if (initialUpdatedTime && currentUpdatedTime !== initialUpdatedTime) {
            throw new Error(`Row ${rowId} has been modified by another user. Please refresh and try again.`);
          }

          return { rowId, currentUpdatedTime };
        } catch (error) {
          console.error(`Failed to check concurrent modification for row ${rowId}:`, error);
          throw error;
        }
      });

      const auditResults = await Promise.all(concurrentModificationChecks);

      // Save each row
      const savePromises = Object.entries(changesByRow).map(async ([rowIdStr, changes]) => {
        const rowId = parseInt(rowIdStr);
        const originalItem = priceListData?.find((item: any) => item.rowId === rowId);
        if (!originalItem) return;

        // Convert values to correct types before updating
        const convertedChanges: any = {};
        Object.entries(changes).forEach(([field, value]) => {
          convertedChanges[field] = convertValueToCorrectType(field, value as string);
        });

        const updatedItem = { ...originalItem, ...convertedChanges };

        return updatePriceListItem({
          productRowId: selectedProductRowId,
          productPriceListRowId: rowId,
          body: updatedItem
        }).unwrap();
      });

      await Promise.all(savePromises);

      // Update the initial timestamps with the new values after successful save
      const newTimestamps = { ...initialUpdatedTimestamps };
      auditResults.forEach(result => {
        if (result) {
          newTimestamps[result.rowId] = result.currentUpdatedTime;
        }
      });
      setInitialUpdatedTimestamps(newTimestamps);

      setChangedCells({});
      refetchPriceList();

      // Show success message
      toast.success('All changes saved successfully!', {
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Save failed:', error);

      // Show user-friendly error message for concurrent modifications
      if (error instanceof Error && error.message.includes('modified by another user')) {
        toast.error(
          '⚠️ Concurrent modification detected! Another user has modified this data. Please refresh and try again.',
          {
            autoClose: 8000,
            closeOnClick: true,
            pauseOnHover: true,
          }
        );
      } else {
        toast.error('❌ Save failed. Please try again.', {
          autoClose: 5000,
        });
      }
    }
  }, [selectedProductRowId, hasUnsavedChanges, changedCells, priceListData, initialUpdatedTimestamps, getProductPriceListAuditDetails, setInitialUpdatedTimestamps, refetchPriceList, updatePriceListItem]);

  // Handle cancel operation
  const handleCancel = useCallback(() => {
    setChangedCells({});
    resetInitialTimestamps();
  }, [resetInitialTimestamps]);

  // Handle refresh product price list with loading state
  const handleRefreshProductPriceList = useCallback(async (params: any) => {
    setIsRefreshingPriceList(true);
    try {
      await refreshProductPriceListMutation(params).unwrap();
    } catch (error) {
      console.error('Failed to refresh product price list:', error);
    } finally {
      setIsRefreshingPriceList(false);
    }
  }, [refreshProductPriceListMutation]);


  const handleRefreshAllPriceLists = useCallback(async (type: string) => {
    if (!priceListData || priceListData.length === 0) {
      return;
    }

    setIsRefreshingPriceList(true);
    try {
      // Send refresh request for each price list item
      // for (const priceListItem of priceListData) {
      //   await handleRefreshProductPriceList({
      //     productRowId: selectedProductRowId,
      //     body: {
      //       priceListId: priceListItem.priceListId,
      //       type: type
      //     }
      //   });
      await handleRefreshProductPriceList({
        productRowId: selectedProductRowId,
        body: {
          priceListId: priceListData[0].priceListId,
          type: type
        }
      });
    } catch (error) {
      console.error('Failed to refresh product price list:', error);
    } finally {
      setIsRefreshingPriceList(false);
    }
  }, [priceListData, selectedProductRowId, handleRefreshProductPriceList]);

  // Handle individual field updates (for checkboxes and dropdowns)
  const handleToggleAllowDiscount = useCallback(async (item: ProductPriceListItem, newValue: boolean) => {
    if (!selectedProductRowId) return;
    setUpdatingItems(prev => new Set(prev).add(item.priceListId));
    try {
      await updatePriceListItem({
        productRowId: selectedProductRowId,
        productPriceListRowId: item.rowId,
        body: { ...item, allowDiscount: newValue }
      }).unwrap();
      refetchPriceList();
    } catch (error) {
      console.error('Failed to update allowDiscount:', error);
    } finally {
      setUpdatingItems(prev => {
        const updated = new Set(prev);
        updated.delete(item.priceListId);
        return updated;
      });
    }
  }, [selectedProductRowId, updatePriceListItem, refetchPriceList]);

  const handleToggleUseVolumePricing = useCallback(async (item: ProductPriceListItem, newValue: boolean) => {
    if (!selectedProductRowId) return;
    setUpdatingItems(prev => new Set(prev).add(item.priceListId));
    try {
      await updatePriceListItem({
        productRowId: selectedProductRowId,
        productPriceListRowId: item.rowId,
        body: { ...item, useVolumePricing: newValue }
      }).unwrap();
      refetchPriceList();
    } catch (error) {
      console.error('Failed to update useVolumePricing:', error);
    } finally {
      setUpdatingItems(prev => {
        const updated = new Set(prev);
        updated.delete(item.priceListId);
        return updated;
      });
    }
  }, [selectedProductRowId, updatePriceListItem, refetchPriceList]);

  const handleTaxFieldChange = useCallback(async (item: ProductPriceListItem, field: 'purchaseTax' | 'defaultTax', newValue: string) => {
    if (!selectedProductRowId) return;
    setUpdatingItems(prev => new Set(prev).add(item.priceListId));
    try {
      await updatePriceListItem({
        productRowId: selectedProductRowId,
        productPriceListRowId: item.rowId,
        body: { ...item, [field]: newValue }
      }).unwrap();
      refetchPriceList();
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
    } finally {
      setUpdatingItems(prev => {
        const updated = new Set(prev);
        updated.delete(item.priceListId);
        return updated;
      });
    }
  }, [selectedProductRowId, updatePriceListItem, refetchPriceList]);

  // Reset changed cells when product changes
  useEffect(() => {
    setChangedCells({});
  }, [selectedProductRowId]);

  return {
    // State
    updatingItems,
    changedCells,
    hasUnsavedChanges,
    isRefreshingPriceList,

    // Actions
    handleCellChange,
    handleSave,
    handleCancel,
    handleToggleAllowDiscount,
    handleToggleUseVolumePricing,
    handleTaxFieldChange,

    // Refresh price list
    handleRefreshProductPriceList,
    handleRefreshAllPriceLists,

    // Helpers
    getCellInfo,
    convertValueToCorrectType

  };
};